/**
 * Typed API client — hand-written shim.
 *
 * TODO: replace with `openapi-typescript-codegen` output once the
 * Rust server exposes a stable `/v1/openapi.json` endpoint. Until
 * that ships, this file provides a minimal, strongly-typed surface
 * covering the endpoints the SPA shell and Wave-2 views will need.
 *
 * Surface covered (FEAT-ADD-010 baseline):
 *   GET  /v1/packets
 *   GET  /v1/packets/{id}/checkpoints
 *   GET  /v1/packets/{id}/artifacts
 *   GET  /v1/artifacts/{id}
 *   GET  /v1/artifacts/{id}/content
 *   POST /v1/artifacts/{id}/verify
 *   GET  /v1/graph/context
 *   GET  /v1/pipelines/{id}/status         (Wave 2: Command Center)
 *   POST /v1/pipelines/{id}/control        (Wave 2: Command Center)
 *   GET  /v1/pipelines/{id}/dag            (Wave 2: Lifecycle)
 *   GET  /v1/dependencies/graph            (Wave 2: Dependency Map)
 *   GET  /v1/activity                      (Wave 2: Activity Feed)
 *   GET  /v1/approvals                     (Wave 2: Approvals)
 *   POST /v1/approvals/{id}                (Wave 2: Approvals)
 *   GET  /metrics                          (Prometheus text — opaque string)
 *
 * All methods accept an optional `AbortSignal` so React effects can
 * cancel in-flight requests on unmount.
 */

import { useAuthStore } from '../stores/auth';
import { useProjectStore } from '../stores/project';
import type {
  AgentActivityPage,
  AgentActivityParams,
  ApprovalSubmitRequest,
  ApprovalSubmitResponse,
  ApprovalSummary,
  DependencyGraph,
  DependencyGraphParams,
  ListApprovalsParams,
  PipelineStatus,
} from './types';

const DEFAULT_BASE_URL = '/';

/** Runtime-read API base. Defaults to same-origin. */
function baseUrl(): string {
  const override = (import.meta.env.VITE_SHAKTI_API_BASE ?? '').trim();
  return override.length > 0 ? override : DEFAULT_BASE_URL;
}

// ── Shared types ────────────────────────────────────────────────────────────

export interface ApiErrorBody {
  error: string;
  code?: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface Packet {
  packet_id: string;
  title: string;
  phase: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface Checkpoint {
  code: string;
  packet_id: string;
  phase: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  decided_at?: string | null;
  decided_by?: string | null;
}

export interface Artifact {
  artifact_id: string;
  packet_id: string;
  kind: string;
  path: string;
  sha256: string;
  size_bytes: number;
  created_at: string;
}

export interface GraphContextRequest {
  query: string;
  limit?: number;
}

export interface GraphNode {
  id: string;
  kind: string;
  label: string;
  attributes: Record<string, string | number | boolean>;
}

export interface GraphEdge {
  source: string;
  target: string;
  kind: string;
  weight?: number;
}

export interface GraphContextResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

// glistening-zebra Phase F1 — Axon impact analysis types. Mirror
// the BFS subgraph + risk score returned by /v1/graph/impact.
export interface GraphImpactRequest {
  node_key: string;
  depth?: number;
}

export interface GraphImpactedNode {
  id: string;
  node_key: string;
  name: string;
  depth: number;
}

export interface GraphImpactResponse {
  impacted_nodes: GraphImpactedNode[];
  impacted_files: string[];
  risk_score: number;
}

// glistening-zebra Phase F1 — agent registry + invocation types.
// `list_agents` returns the projection produced by routes.rs:1839;
// the format-strings on the server side mean these come through
// as already-stringified Debug output, not enum variants. Treat
// them as opaque labels.
export interface AgentDescriptor {
  agent_name: string;
  capabilities: string;
  autonomy_rung: string;
  max_ralph_iterations: number;
  prompt_template_key: string;
  health_state: string;
}

export interface AgentDescriptorList {
  agents: AgentDescriptor[];
  total: number;
}

export interface InvokeAgentResponse {
  execution_id: string;
  // Server returns more fields; the planning-chat route only needs
  // execution_id to deep-link into the activity stream. Other
  // fields are typed as `unknown` until a consumer needs them.
  [key: string]: unknown;
}

// ── Core request helper ─────────────────────────────────────────────────────

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Override content negotiation for opaque endpoints (e.g. Prometheus text). */
  expect?: 'json' | 'text';
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, headers = {}, expect = 'json' } = options;

  const url = joinUrl(baseUrl(), path);
  const token = useAuthStore.getState().token;

  const finalHeaders: Record<string, string> = {
    Accept: expect === 'json' ? 'application/json' : 'text/plain',
    ...headers,
  };

  if (token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  // F2.1 — thread active project_id as X-Project-Id so the tenant
  // middleware can validate it against the caller's org. Skipped if
  // the caller already provided a header (tests, or per-call
  // overrides).
  if (!finalHeaders['X-Project-Id']) {
    const activeProjectId = useProjectStore.getState().activeProjectId;
    if (typeof activeProjectId === 'string' && activeProjectId.length > 0) {
      finalHeaders['X-Project-Id'] = activeProjectId;
    }
  }

  const init: RequestInit = {
    method,
    headers: finalHeaders,
    credentials: 'include',
    ...(signal ? { signal } : {}),
  };

  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    const errBody = await parseErrorBody(response);
    throw new ApiError(
      response.status,
      errBody,
      errBody?.error ?? `${method} ${path} failed with ${response.status}`,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (expect === 'text') {
    return (await response.text()) as unknown as T;
  }

  return (await response.json()) as T;
}

async function parseErrorBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as ApiErrorBody;
    } catch {
      return { error: text };
    }
  } catch {
    return null;
  }
}

function joinUrl(base: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const b = base.endsWith('/') ? base.slice(0, -1) : base;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

// ── Public API surface ──────────────────────────────────────────────────────

export interface ListPacketsParams {
  limit?: number;
  cursor?: string;
  signal?: AbortSignal;
}

export const shaktiApi = {
  /** F2.1 — list projects for the active caller's org. Used by ProjectSwitcher. */
  async listProjects(
    opts: { signal?: AbortSignal; organizationId?: string } = {},
  ): Promise<Array<{ id: string; name: string; slug: string; organization_id: string }>> {
    const search = new URLSearchParams();
    if (opts.organizationId) search.set('organization_id', opts.organizationId);
    const qs = search.toString();
    const path = `/v1/projects${qs ? `?${qs}` : ''}`;
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<Array<{ id: string; name: string; slug: string; organization_id: string }>>(
      path,
      reqOpts,
    );
  },

  /**
   * N5.3 (round-6) — fetch the project summary rendered by the home
   * route. Aggregates packet count, latest phase, pending approvals,
   * tokens consumed this period, and recent activity for the given
   * project. The server computes this server-side via the repos so
   * the dashboard stays honest even when the Axon graph is absent.
   */
  async getProjectSummary(
    projectId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    id: string;
    name: string;
    description: string | null;
    active_packets: number;
    latest_phase_number: number | null;
    latest_phase_name: string | null;
    pending_approvals: number;
    tokens_period: number | null;
    cost_period_usd: number | null;
    recent_activity: Array<{ id: string; action: string; occurred_at: string }>;
  }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(`/v1/projects/${projectId}/summary`, reqOpts);
  },

  async listPackets(params: ListPacketsParams = {}): Promise<Packet[]> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.cursor !== undefined) search.set('cursor', params.cursor);
    const qs = search.toString();
    const path = `/v1/packets${qs ? `?${qs}` : ''}`;
    const opts: RequestOptions = {};
    if (params.signal) opts.signal = params.signal;
    return request<Packet[]>(path, opts);
  },

  async listCheckpoints(
    packetId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Checkpoint[]> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<Checkpoint[]>(
      `/v1/packets/${encodeURIComponent(packetId)}/checkpoints`,
      reqOpts,
    );
  },

  async listArtifacts(
    packetId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Artifact[]> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<Artifact[]>(
      `/v1/packets/${encodeURIComponent(packetId)}/artifacts`,
      reqOpts,
    );
  },

  async getArtifact(
    artifactId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Artifact> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<Artifact>(`/v1/artifacts/${encodeURIComponent(artifactId)}`, reqOpts);
  },

  async graphContext(
    body: GraphContextRequest,
    opts: { signal?: AbortSignal } = {},
  ): Promise<GraphContextResponse> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<GraphContextResponse>('/v1/graph/context', reqOpts);
  },

  // glistening-zebra Phase F1 — Axon impact analysis. Drives the
  // /dashboard/impact route. Default depth=3 matches the server-
  // side default in routes.rs::graph_impact (line 1406).
  async graphImpact(
    body: GraphImpactRequest,
    opts: { signal?: AbortSignal } = {},
  ): Promise<GraphImpactResponse> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<GraphImpactResponse>('/v1/graph/impact', reqOpts);
  },

  // glistening-zebra Phase F1 — agent invocation surface. The
  // dashboard already streams agent *output* over the
  // /ws/agent-activity channel (see ws.ts); these two methods
  // close the loop by letting the planning-chat route discover
  // available agents and dispatch new invocations against them.
  async listAgents(
    opts: { signal?: AbortSignal } = {},
  ): Promise<AgentDescriptorList> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<AgentDescriptorList>('/v1/agents', reqOpts);
  },

  async invokeAgent(
    agentName: string,
    input: unknown,
    opts: { branch?: string; signal?: AbortSignal } = {},
  ): Promise<InvokeAgentResponse> {
    const body: { input: unknown; branch?: string } = { input };
    if (opts.branch) body.branch = opts.branch;
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<InvokeAgentResponse>(
      `/v1/agents/${encodeURIComponent(agentName)}/invoke`,
      reqOpts,
    );
  },

  async metricsText(opts: { signal?: AbortSignal } = {}): Promise<string> {
    const reqOpts: RequestOptions = { expect: 'text' };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<string>('/metrics', reqOpts);
  },

  // ── Artifact content / verification ──────────────────────────────────────

  /** Raw artifact bytes as a string (caller handles decoding). */
  async getArtifactContent(
    artifactId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<string> {
    const reqOpts: RequestOptions = { expect: 'text' };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<string>(
      `/v1/artifacts/${encodeURIComponent(artifactId)}/content`,
      reqOpts,
    );
  },

  async verifyArtifact(
    artifactId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ verified: boolean; sha256: string }> {
    const reqOpts: RequestOptions = { method: 'POST' };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<{ verified: boolean; sha256: string }>(
      `/v1/artifacts/${encodeURIComponent(artifactId)}/verify`,
      reqOpts,
    );
  },

  // ── Pipeline status / control (Wave 2: Command Center) ───────────────────

  async getPipelineStatus(
    pipelineId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<PipelineStatus> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<PipelineStatus>(
      `/v1/pipelines/${encodeURIComponent(pipelineId)}/status`,
      reqOpts,
    );
  },

  /**
   * Pause / resume / cancel a pipeline. The backend is responsible for
   * idempotency — re-issuing `pause` on an already-paused pipeline is a no-op.
   */
  async controlPipeline(
    pipelineId: string,
    action: 'pause' | 'resume' | 'cancel',
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ pipeline_id: string; status: string }> {
    const reqOpts: RequestOptions = { method: 'POST', body: { action } };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<{ pipeline_id: string; status: string }>(
      `/v1/pipelines/${encodeURIComponent(pipelineId)}/control`,
      reqOpts,
    );
  },

  /** DAG projection for the Lifecycle view. */
  async getPipelineDag(
    pipelineId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    nodes: Array<{ id: string; label: string; status: string }>;
    edges: Array<{ source: string; target: string }>;
  }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(
      `/v1/pipelines/${encodeURIComponent(pipelineId)}/dag`,
      reqOpts,
    );
  },

  // ── Dependency graph (Wave 2: Dependency Map) ─────────────────────────────

  async getDependencyGraph(
    params: DependencyGraphParams & { signal?: AbortSignal } = {},
  ): Promise<DependencyGraph> {
    const search = new URLSearchParams();
    if (params.packet_id !== undefined) search.set('packet_id', params.packet_id);
    if (params.module !== undefined) search.set('module', params.module);
    if (params.phase !== undefined) search.set('phase', params.phase);
    const qs = search.toString();
    const path = `/v1/dependencies/graph${qs ? `?${qs}` : ''}`;
    const reqOpts: RequestOptions = {};
    if (params.signal) reqOpts.signal = params.signal;
    return request<DependencyGraph>(path, reqOpts);
  },

  // ── Agent activity feed (Wave 2: Activity) ────────────────────────────────

  async listAgentActivity(
    params: AgentActivityParams & { signal?: AbortSignal } = {},
  ): Promise<AgentActivityPage> {
    const search = new URLSearchParams();
    if (params.cursor !== undefined) search.set('cursor', params.cursor);
    if (params.packet_id !== undefined) search.set('packet_id', params.packet_id);
    if (params.agent !== undefined) search.set('agent', params.agent);
    if (params.severity !== undefined) search.set('severity', params.severity);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    const path = `/v1/activity${qs ? `?${qs}` : ''}`;
    const reqOpts: RequestOptions = {};
    if (params.signal) reqOpts.signal = params.signal;
    return request<AgentActivityPage>(path, reqOpts);
  },

  // ── Approvals (Wave 2: Approval Panel) ────────────────────────────────────

  async listApprovals(
    params: ListApprovalsParams & { signal?: AbortSignal } = {},
  ): Promise<ApprovalSummary[]> {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    if (params.packet_id !== undefined) search.set('packet_id', params.packet_id);
    const qs = search.toString();
    const path = `/v1/approvals${qs ? `?${qs}` : ''}`;
    const reqOpts: RequestOptions = {};
    if (params.signal) reqOpts.signal = params.signal;
    return request<ApprovalSummary[]>(path, reqOpts);
  },

  async submitApproval(
    approvalId: string,
    body: ApprovalSubmitRequest,
    opts: { signal?: AbortSignal } = {},
  ): Promise<ApprovalSubmitResponse> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<ApprovalSubmitResponse>(
      `/v1/approvals/${encodeURIComponent(approvalId)}`,
      reqOpts,
    );
  },

  // ── H3.4 (round-4) — projects ─────────────────────────────────────────────

  async createProject(
    body: { name: string; slug: string; settings?: Record<string, unknown> },
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ id: string; name: string; slug: string; organization_id: string }> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/projects', reqOpts);
  },

  // ── H3.3 (round-4) — providers ────────────────────────────────────────────

  async listProviders(opts: { signal?: AbortSignal } = {}): Promise<string[]> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request<string[]>('/v1/providers', reqOpts);
  },

  async registerProvider(
    body: {
      provider: string;
      api_key: string;
      base_url?: string | null;
      extra?: Record<string, unknown>;
    },
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ provider: string; status: string; activated: boolean }> {
    const reqOpts: RequestOptions = {
      method: 'POST',
      body: {
        ...body,
        base_url: body.base_url ?? null,
        extra: body.extra ?? {},
      },
    };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/providers', reqOpts);
  },

  async removeProvider(
    provider: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<void> {
    const reqOpts: RequestOptions = { method: 'DELETE' };
    if (opts.signal) reqOpts.signal = opts.signal;
    await request<unknown>(
      `/v1/providers/${encodeURIComponent(provider)}`,
      reqOpts,
    );
  },

  // ── H2.2 (round-4) — API keys ─────────────────────────────────────────────

  async createApiKey(
    body: {
      label: string;
      scopes?: string[];
      rate_limit_rpm?: number;
      expires_at?: string | null;
      test_mode?: boolean;
    },
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    key: string;
    key_preview: string;
    key_id: string;
    label: string;
    scopes: string[];
    rate_limit_rpm: number;
    created_at: string;
    expires_at: string | null;
  }> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/api-keys', reqOpts);
  },

  async listApiKeys(opts: { signal?: AbortSignal } = {}): Promise<
    Array<{
      key_id: string;
      key_preview: string;
      label: string;
      scopes: string[];
      rate_limit_rpm: number;
      created_at: string;
      last_used_at: string | null;
      expires_at: string | null;
      active: boolean;
    }>
  > {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/api-keys', reqOpts);
  },

  async revokeApiKey(
    keyId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<void> {
    const reqOpts: RequestOptions = { method: 'DELETE' };
    if (opts.signal) reqOpts.signal = opts.signal;
    await request<unknown>(
      `/v1/api-keys/${encodeURIComponent(keyId)}`,
      reqOpts,
    );
  },

  // ── H3.5 (round-4) — packets + pipelines ──────────────────────────────────

  async createPacket(
    body: {
      charter_ref: string;
      title: string;
      evidence_path?: string | null;
      intent?: string | null;
    },
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    id: string;
    packet_ref: string;
    title: string;
    current_phase: number;
    status: string;
    created_at: string;
  }> {
    const reqOpts: RequestOptions = {
      method: 'POST',
      body: { ...body, evidence_path: body.evidence_path ?? null },
    };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/packets', reqOpts);
  },

  async runFullPipeline(
    packetId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ packet_id: string; chain_id: string; phases: string[] }> {
    const reqOpts: RequestOptions = { method: 'POST' };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(
      `/v1/packets/${encodeURIComponent(packetId)}/run-full-pipeline`,
      reqOpts,
    );
  },

  async getAuthMe(
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    user_id: string | null;
    org_id: string | null;
    role: string;
    authenticated: boolean;
  }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/auth/me', reqOpts);
  },

  // ── K2.1 (round-5) — provider test-connection ─────────────────────────────

  async pingProvider(
    provider: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    provider: string;
    ok: boolean;
    latency_ms: number;
    wall_ms: number;
    error: string | null;
  }> {
    const reqOpts: RequestOptions = { method: 'POST' };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(`/v1/providers/${encodeURIComponent(provider)}/ping`, reqOpts);
  },

  // ── K2.2 (round-5) — VCS credentials ──────────────────────────────────────

  async createVcsCredential(
    body: {
      project_id: string;
      provider: 'github' | 'gitlab';
      base_url?: string | null;
      pat: string;
      label: string;
      scopes?: string[];
      expires_at?: string | null;
    },
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    id: string;
    project_id: string;
    provider: string;
    base_url: string;
    label: string;
    scopes: string[];
    created_at: string;
    rotated_at: string | null;
    expires_at: string | null;
  }> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/vcs-credentials', reqOpts);
  },

  async listVcsCredentials(opts: { signal?: AbortSignal } = {}): Promise<
    Array<{
      id: string;
      project_id: string;
      provider: string;
      base_url: string;
      label: string;
      scopes: string[];
      created_at: string;
      rotated_at: string | null;
      expires_at: string | null;
    }>
  > {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/vcs-credentials', reqOpts);
  },

  async deleteVcsCredential(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<void> {
    const reqOpts: RequestOptions = { method: 'DELETE' };
    if (opts.signal) reqOpts.signal = opts.signal;
    await request<unknown>(
      `/v1/vcs-credentials/${encodeURIComponent(id)}`,
      reqOpts,
    );
  },

  // ── K1.4 (round-5) — deployment artifacts ─────────────────────────────────

  async listDeploymentArtifacts(
    projectId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<
    Array<{
      id: string;
      project_id: string;
      kind: string;
      path: string;
      size_bytes: number;
      created_at: string;
    }>
  > {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(
      `/v1/deploy/artifacts?project_id=${encodeURIComponent(projectId)}`,
      reqOpts,
    );
  },

  async generateDeployment(
    body: { project_id: string; app_name?: string; target?: 'helm' | 'docker' },
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ project_id: string; artifacts: Array<{ path: string; kind: string }> }> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/deploy/generate', reqOpts);
  },

  // ── K3.5 (round-5) — billing usage / cost dashboard ───────────────────────

  // ── L4.3 (round-5) — admin view of captured marketing leads ───────────────

  async listMarketingLeads(
    leadType?: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    leads: Array<{
      id: string;
      email: string;
      lead_type: string;
      company: string | null;
      role: string | null;
      source_page: string | null;
      created_at: string;
    }>;
    count: number;
  }> {
    const qs = leadType ? `?lead_type=${encodeURIComponent(leadType)}` : '';
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(`/v1/marketing/leads${qs}`, reqOpts);
  },

  async getBillingUsage(
    params: { from?: string; to?: string; group_by?: 'provider' | 'agent' | 'day' } = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    total_usd: number;
    group_by: string;
    rows: Array<{ bucket: string; tokens_in: number; tokens_out: number; cost_usd: number }>;
  }> {
    const search = new URLSearchParams();
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    if (params.group_by) search.set('group_by', params.group_by);
    const qs = search.toString();
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(`/v1/billing/usage${qs ? `?${qs}` : ''}`, reqOpts);
  },

  // ── Round-6 close-out helpers (#112, #116, #117, #118, #119, #121) ────────

  /**
   * #116 — surface Trust L0 attestation snapshot captured at server boot.
   * Dashboard shows this in the top nav + Trust card.
   */
  async getTrustStatus(opts: { signal?: AbortSignal } = {}): Promise<{
    backend: string;
    verified: boolean;
    reason: string | null;
    checked_at: string;
    quote_len: number;
  }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/trust/status', reqOpts);
  },

  /**
   * #121 — taint summary replaces the static TAINT_SUMMARY constant on
   * the data-residency page.
   */
  async getTaintSummary(opts: { signal?: AbortSignal } = {}): Promise<{
    organization_id: string;
    generated_at: string;
    totals: {
      tracked_resources: number;
      dsar_requests_last_90d: number;
      audit_entries: number;
    };
    by_kind: Array<{ kind: string; count: number }>;
  }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/taint/summary', reqOpts);
  },

  /**
   * T4.2 (v2.0) — helm release status for the dashboard Deploy page.
   */
  async helmStatus(
    release: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    release: string;
    phase: string;
    revision: number | null;
    last_deployed: string | null;
    notes: string | null;
  }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(`/v1/deploy/status/${encodeURIComponent(release)}`, reqOpts);
  },

  /**
   * T4.3 (v2.0) — one-click helm rollback.
   */
  async helmRollback(
    release: string,
    body: { revision?: number | null; timeout_secs?: number } = {},
  ): Promise<{
    success: boolean;
    previous_revision: number | null;
    rolled_back_to: number | null;
    stdout: string;
    stderr: string;
  }> {
    return request(`/v1/deploy/rollback/${encodeURIComponent(release)}`, {
      method: 'POST',
      body,
    });
  },

  /**
   * T4.4 (v2.0) — list per-project cluster targets.
   */
  async listClusterConfigs(
    opts: { signal?: AbortSignal } = {},
  ): Promise<
    Array<{
      id: string;
      project_id: string;
      label: string;
      environment: string;
      kube_context: string | null;
      default_namespace: string;
      created_at: string;
      updated_at: string;
    }>
  > {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/deploy/cluster-configs', reqOpts);
  },

  /**
   * T4.4 (v2.0) — add a new cluster target. Body carries raw
   * kubeconfig YAML; the server seals it under the master KEK.
   */
  async createClusterConfig(body: {
    label: string;
    environment?: string;
    kubeconfig_yaml: string;
    kube_context?: string;
    default_namespace?: string;
  }): Promise<{
    id: string;
    project_id: string;
    label: string;
    environment: string;
    kube_context: string | null;
    default_namespace: string;
    created_at: string;
    updated_at: string;
  }> {
    return request('/v1/deploy/cluster-configs', {
      method: 'POST',
      body,
    });
  },

  /**
   * T4.4 (v2.0) — remove a cluster target.
   */
  async deleteClusterConfig(id: string): Promise<void> {
    return request(`/v1/deploy/cluster-configs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * T1 (v2.0) — admin snapshot of every active rate-limit cell.
   * Feeds the `/dashboard/rate-limits` page. Admin-gated server-side.
   */
  async getRateLimitsUsage(
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    cells: Array<{
      dimension: string;
      entity_id: string;
      limit: number;
      period_secs: number;
      burst: number;
      current_load_ratio: number;
      last_accessed: string;
    }>;
    total_active_cells: number;
    rate_limit_hits_total: number;
  }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/admin/rate-limits/usage', reqOpts);
  },

  /**
   * #117 — proxy helm install/upgrade through the sidecar.
   */
  async helmInstall(body: {
    release: string;
    chart: string;
    namespace: string;
    values_file?: string | null;
    set_values?: string[];
    upgrade?: boolean;
  }): Promise<{
    success: boolean;
    stdout: string;
    stderr: string;
    exit_code: number | null;
    proxied_to: string;
  }> {
    return request('/v1/deploy/helm-install', {
      method: 'POST',
      body,
    });
  },

  /**
   * #112 — per-agent autonomy rung (1–5).
   */
  async setAgentAutonomy(agentId: string, rung: 1 | 2 | 3 | 4 | 5): Promise<{
    agent_id: string;
    rung: number;
    updated_at: string;
  }> {
    return request(`/v1/agents/${encodeURIComponent(agentId)}/autonomy`, {
      method: 'POST',
      body: { rung },
    });
  },

  /**
   * W1.2 (v1.1) — read the persisted rung for one agent. Defaults
   * to rung 2 when no row exists yet, so the slider hydrates
   * correctly without a pre-warm write.
   */
  async getAgentAutonomy(
    agentId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ agent_id: string; rung: number; updated_at: string }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(
      `/v1/agents/${encodeURIComponent(agentId)}/autonomy`,
      reqOpts,
    );
  },

  /**
   * W1.2 (v1.1) — load every persisted rung for the active tenant
   * in one round-trip so the dashboard can seed all AutonomyLadder
   * sliders at once. Agents without a row fall back to `default_rung`.
   */
  async listAgentAutonomy(
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    rungs: Record<string, number>;
    default_rung: number;
    updated_at: string | null;
  }> {
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request('/v1/agents/autonomy', reqOpts);
  },

  /**
   * #118 — mint a short-lived WS ticket for /ws/* subscriptions.
   */
  async mintWsTicket(body: {
    organization_id: string;
    project_id?: string;
  }): Promise<{ token: string; expires_at: string }> {
    return request('/v1/auth/ws-ticket', {
      method: 'POST',
      body,
    });
  },

  /**
   * #119 — SCIM token CRUD (exposed via the new Settings → SCIM tab).
   */
  async listScimTokens(): Promise<Array<{
    id: string;
    label: string;
    created_at: string;
    last_used_at: string | null;
    revoked_at: string | null;
    token_preview: string;
  }>> {
    // Backend endpoint TBD in later tab impl; this shape mirrors
    // scim_tokens table + a preview prefix so the dashboard doesn't
    // ship the raw hash.
    return request('/v1/scim/tokens');
  },

  async createScimToken(body: {
    label: string;
  }): Promise<{ id: string; raw_token: string; label: string }> {
    return request('/v1/scim/tokens', { method: 'POST', body });
  },

  async revokeScimToken(id: string): Promise<void> {
    await request(`/v1/scim/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  /**
   * #119 — SAML IdP metadata ingest + SP metadata download.
   */
  async uploadSamlIdpMetadata(body: {
    metadata_xml: string;
  }): Promise<{
    entity_id: string;
    sso_url: string;
    slo_url: string | null;
  }> {
    return request('/v1/auth/saml/idp-metadata', { method: 'POST', body });
  },

  /**
   * W2.5 (v1.1) — kick off SP-initiated SAML login. Response
   * includes the IdP redirect URL; the caller should navigate to
   * it via `window.location.href = redirect_url`.
   */
  async samlLogin(
    organizationId: string,
    returnTo?: string,
  ): Promise<{ redirect_url: string; relay_state: string; request_id: string }> {
    return request('/v1/auth/saml/login', {
      method: 'POST',
      body: {
        organization_id: organizationId,
        return_to: returnTo,
      },
    });
  },

  /**
   * W2.5 (v1.1) — SP-initiated single logout. Returns the IdP SLO
   * URL the caller should redirect to so the IdP tears down its
   * session as well.
   */
  async samlSlo(
    organizationId: string,
    relayState?: string,
  ): Promise<{ redirect_url: string | null; acknowledged: boolean }> {
    const params = new URLSearchParams();
    if (relayState) params.set('RelayState', relayState);
    return request(`/v1/auth/saml/slo/${encodeURIComponent(organizationId)}`, {
      method: 'POST',
      body: params.toString(),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    });
  },

  /**
   * #119 — DSAR export / erase.
   */
  async dsarExport(subjectEmail: string): Promise<{
    subject_email: string;
    organization_id: string;
    served_at: string;
    user_rows: unknown[];
    email_rows: unknown[];
    audit_entry_count: number;
    model_usage_token_total: number;
  }> {
    return request('/v1/compliance/dsar', {
      method: 'POST',
      body: { subject_email: subjectEmail },
    });
  },

  async dsarErase(subjectEmail: string): Promise<{
    subject_email: string;
    users_soft_deleted: number;
    hard_delete_at: string;
  }> {
    return request('/v1/compliance/dsar/erase', {
      method: 'POST',
      body: { subject_email: subjectEmail },
    });
  },

  // ── K5.1 (round-5) — audit trail + evidence bundle export ─────────────────

  async getAuditTrail(
    params: { packet_id?: string; from?: string; to?: string } = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    entries: Array<{
      id: string;
      packet_id: string | null;
      actor: string;
      event_type: string;
      timestamp: string;
      hash: string;
      prev_hash: string | null;
      payload: Record<string, unknown>;
    }>;
    chain_verified: boolean;
  }> {
    const search = new URLSearchParams();
    if (params.packet_id) search.set('packet_id', params.packet_id);
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    const qs = search.toString();
    const reqOpts: RequestOptions = {};
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(`/v1/audit/trail${qs ? `?${qs}` : ''}`, reqOpts);
  },

  // ── K1.3 (round-5) — VCS push + PR ────────────────────────────────────────

  async vcsPush(
    packetId: string,
    body: {
      project_id: string;
      provider: string;
      repo_owner: string;
      repo_name: string;
      branch: string;
      head_sha: string;
      force?: boolean;
    },
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ provider: string; pushed_sha: string }> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(`/v1/packets/${encodeURIComponent(packetId)}/push`, reqOpts);
  },

  async vcsOpenPr(
    packetId: string,
    body: {
      project_id: string;
      provider: string;
      repo_owner: string;
      repo_name: string;
      source_branch: string;
      target_branch: string;
      title: string;
      body: string;
      auto_merge?: boolean;
      merge_method?: 'merge' | 'squash' | 'rebase';
    },
    opts: { signal?: AbortSignal } = {},
  ): Promise<{
    provider: string;
    pr: { number: number; html_url: string; head_sha: string; state: string };
    // K3.4 (round-5) — present when auto_merge was requested. When the
    // remote declines (CI red, branch protection, conflict), merged=false
    // and message carries the human-readable reason.
    merge?: {
      merged: boolean;
      method: string;
      message?: string;
      sha?: string;
    };
  }> {
    const reqOpts: RequestOptions = { method: 'POST', body };
    if (opts.signal) reqOpts.signal = opts.signal;
    return request(`/v1/packets/${encodeURIComponent(packetId)}/open-pr`, reqOpts);
  },

  /** Returns a signed evidence bundle (Blob) suitable for SOC 2 export. */
  async exportAuditBundle(packetId: string): Promise<Blob> {
    const token = useAuthStore.getState().token;
    const orgId = useAuthStore.getState().organizationId;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (orgId) headers['X-Org-Id'] = orgId;
    const projectId = useProjectStore.getState().activeProjectId;
    if (projectId) headers['X-Project-Id'] = projectId;
    const resp = await fetch(
      `${baseUrl().replace(/\/$/, '')}/v1/audit/export/${encodeURIComponent(packetId)}`,
      { headers },
    );
    if (!resp.ok) {
      throw new ApiError(resp.status, null, `audit export failed: ${resp.status}`);
    }
    return resp.blob();
  },
} as const;

export type ShaktiApi = typeof shaktiApi;
